import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthModal from '../components/AuthModal';
import {
  Sparkles, ArrowRight, ChevronDown, Lock, Shield, Award, Clock, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { FEATURES, LOTTERIES, PLANS, STEPS, TESTIMONIALS, FAQS } from '../components/landing/landingData';
import { PricingSection } from '../components/landing/PricingSection';
import { TestimonialsSection, FAQSection, CTASection, FooterSection } from '../components/landing/LandingSections';

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [openFaq, setOpenFaq] = useState(null);
  const [lotteryFeed, setLotteryFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [dailyNumbers, setDailyNumbers] = useState([]);
  const [dailyMeta, setDailyMeta] = useState(null);

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode && !user) { setAuthMode('register'); setShowAuth(true); }
  }, [searchParams, user]);

  // Cargar feed publico de loterias con ultimos resultados
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public-feed/lotteries`, { timeout: 8000 });
        if (mounted) setLotteryFeed(res.data || []);
      } catch (e) { if (mounted) setLotteryFeed([]); }
      finally { if (mounted) setFeedLoading(false); }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  // Cargar 6 numeros del Robot Diario (generados a las 6 AM RD)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public-feed/daily-numbers`, { timeout: 60000 });
        if (mounted) {
          setDailyNumbers(res.data?.available ? (res.data.numbers || []) : []);
          setDailyMeta(res.data?.available ? {
            date: res.data.date,
            lotteries: res.data.lotteries_analyzed,
            sourceDate: res.data.source_latest_date,
            generatedAt: res.data.generated_at,
          } : null);
        }
      } catch (e) { if (mounted) { setDailyNumbers([]); setDailyMeta(null); } }
    };
    load();
    // Refrescar cada 5 min por si el robot acaba de generar
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  const openAuthModal = (mode = 'register') => { setAuthMode(mode); setShowAuth(true); };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      // Formato completo: DD/MM/YYYY (ej: 17/06/2026)
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = d.getUTCFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return iso.slice(0, 10); }
  };

  // Generate initials avatar from lottery name (used when no logo_url available)
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/).filter(w => !/^\d+:?\d*$/.test(w)); // skip times like 12:30
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1a2744' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-sky-900/30" style={{ backgroundColor: 'rgba(26, 39, 68, 0.9)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src="/logo.png" alt="NumeroMaestro" className="h-12 w-auto" />
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sky-300 hover:text-white transition-colors">Caracteristicas</a>
            <a href="#pricing" className="text-sky-300 hover:text-white transition-colors">Precios</a>
            <a href="#faq" className="text-sky-300 hover:text-white transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-sky-400 text-sky-300 hover:bg-sky-500/20 hover:text-white font-semibold" onClick={() => openAuthModal('login')}>Iniciar Sesion</Button>
            <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white" onClick={() => openAuthModal('register')}>Comenzar</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-900/20 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-sky-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative z-10 max-w-[1600px] mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_1fr] gap-8 items-start">
          {/* LEFT — Hero content (angosto) */}
          <div className="text-center lg:text-left lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-full mb-6">
              <Sparkles size={14} className="text-sky-400" />
              <span className="text-sky-400 text-xs font-medium">Impulsado por Sócrates</span>
            </div>
            <div className="mb-6 flex justify-center lg:justify-start"><img src="/logo.png" alt="NumeroMaestro" className="h-28 w-auto" /></div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 tracking-tight" data-testid="hero-title">NumeroMaestro</h1>
            <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-sky-400 to-orange-400 bg-clip-text text-transparent mb-4">Tu Suerte, Tu Estrategia</p>
            <p className="text-sm text-sky-200/70 mb-8">
              Sistema inteligente de analisis con Sócrates para todas las loterias dominicanas, Florida y New York. Predicciones en tiempo real con consenso de 3 motores.
            </p>
            <div className="mb-8">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                <p className="text-sky-300/80 text-xs font-bold uppercase tracking-widest">
                  {dailyNumbers.length > 0 ? `🎯 Números Maestro del día · ${dailyMeta?.date?.split('-').reverse().join('/') || ''}` : 'Números recomendados del día'}
                </p>
                {dailyMeta?.lotteries > 0 && (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold">
                    {dailyMeta.lotteries} loterías
                  </Badge>
                )}
              </div>
              {dailyNumbers.length === 6 ? <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto lg:mx-0">
                {dailyNumbers.map((num, idx) => (
                  <div key={`day-${num}-${idx}`}
                    className={`w-full aspect-square shrink-0 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform ${
                      idx % 2 === 0
                        ? 'bg-gradient-to-br from-emerald-400 to-green-600 shadow-emerald-500/40'
                        : 'bg-gradient-to-br from-sky-400 to-sky-600 shadow-sky-500/30'
                    }`}>
                    <span className="text-2xl font-black text-white">{num.toString().padStart(2, '0')}</span>
                  </div>
                ))}
              </div> : <p className="text-sky-200/70 text-sm">Los números del día aún no están disponibles.</p>}
              {dailyNumbers.length === 6 && (
                <p className="text-center lg:text-left text-emerald-400/70 text-[10px] mt-2 leading-relaxed">
                  Selección basada en {dailyMeta?.lotteries} loterías. Datos hasta {dailyMeta?.sourceDate?.split('-').reverse().join('/') || 'fecha no disponible'}; preparada a las {dailyMeta?.generatedAt ? new Date(dailyMeta.generatedAt).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Santo_Domingo' }) : 'hora no disponible'} (RD). No es una probabilidad de acierto; confirma horario y resultados antes de jugar.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Button onClick={() => openAuthModal('register')} size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-base px-6 py-6 rounded-xl shadow-lg shadow-orange-500/30" data-testid="get-started-btn">
                Comenzar Ahora <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button variant="outline" size="lg" className="border-sky-700 text-sky-300 hover:bg-sky-900/50 text-base px-6 py-6 rounded-xl" onClick={() => openAuthModal('login')} data-testid="login-btn">Ya tengo cuenta</Button>
            </div>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4 text-sky-300/60">
              <div className="flex items-center gap-1.5"><Lock size={14} className="text-sky-400" /><span className="text-xs">Pagos Seguros</span></div>
              <div className="flex items-center gap-1.5"><Shield size={14} className="text-orange-400" /><span className="text-xs">Datos Protegidos</span></div>
              <div className="flex items-center gap-1.5"><Award size={14} className="text-sky-400" /><span className="text-xs">+10,000</span></div>
            </div>
          </div>

          {/* RIGHT — Loterías Dominicanas (ancho) */}
          <div data-testid="lottery-feed-panel">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white text-center mb-3 tracking-tight" data-testid="lottery-feed-title">
              Loterías Dominicanas
            </h2>
            <p className="text-center text-sky-300/60 text-sm mb-8 flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Últimos resultados disponibles · Consultados cada minuto
            </p>

            {feedLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={`sk-${i}`} className="h-48 rounded-xl bg-slate-800/40 animate-pulse border border-slate-700/30" />
                ))}
              </div>
            ) : lotteryFeed.length === 0 ? (
              <p className="text-center text-sky-200/70 py-12">Los resultados aún no están disponibles. Vuelve a consultar más tarde.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {lotteryFeed.map((lot) => {
                  const isToday = !!lot.last_result?.is_today;
                  const ballColor = isToday ? '#22c55e' : '#6b7280';
                  return (
                  <div key={lot.id}
                    data-testid={`feed-lottery-${lot.id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-200 cursor-pointer hover:-translate-y-1"
                    onClick={() => openAuthModal('register')}>
                    {/* Top color stripe with company name */}
                    <div className="px-2 sm:px-3 py-2 sm:py-2.5 text-center text-xs sm:text-sm md:text-base font-black text-white tracking-wide" style={{ backgroundColor: lot.color || '#22c55e' }}>
                      {lot.company || lot.name}
                    </div>
                    {/* Body */}
                    <div className="p-2.5 sm:p-3 md:p-4">
                      <div className="flex items-start justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-500 text-[10px] sm:text-xs flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                            <span>{formatDate(lot.last_result?.draw_date)}</span>
                            <Clock size={9} className="text-slate-400" />
                            <span>{lot.draw_time}</span>
                          </div>
                          <div className="text-slate-900 font-black text-xs sm:text-sm md:text-base leading-tight truncate" title={lot.name}>{lot.name}</div>
                        </div>
                        {lot.logo_url ? (
                          <img src={lot.logo_url} alt="" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl object-cover shrink-0 ring-1 sm:ring-2 ring-slate-100" loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 aspect-square shrink-0 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-white text-sm sm:text-base md:text-lg shadow-md ring-1 sm:ring-2 ring-white"
                            style={{ backgroundColor: lot.color || '#22c55e' }}
                            aria-label={lot.name}>
                            {getInitials(lot.company || lot.name)}
                          </div>
                        )}
                      </div>
                      {/* Numbers — responsive sizing */}
                      {lot.last_result?.numbers?.length > 0 ? (
                        <div className="flex items-center justify-start gap-1.5 sm:gap-2 md:gap-3">
                          {lot.last_result.numbers.slice(0, 3).map((n, ni) => (
                            <div key={`${lot.id}-n-${ni}`}
                              className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 aspect-square shrink-0 rounded-full flex items-center justify-center shadow-md font-black text-sm sm:text-base md:text-lg text-white"
                              style={{ backgroundColor: ballColor }}>
                              {String(n).padStart(2, '0')}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center h-10 sm:h-12 md:h-14">
                          <div className="text-slate-400 text-xs sm:text-sm italic">Sin resultado aún</div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce"><ChevronDown size={24} className="text-sky-600" /></div>
      </section>

      {/* Lotteries */}
      <section className="py-20 border-t border-sky-900/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 mb-4">20+ Loterias</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Todas las Loterias en un Solo Lugar</h2>
            <p className="text-sky-200/60 max-w-2xl mx-auto">Republica Dominicana, Florida y New York. Actualizadas cada 5 minutos.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {LOTTERIES.map((lottery, idx) => (
              <div key={`lot-${idx}`} className="group p-5 bg-sky-900/20 border border-sky-800/30 rounded-2xl hover:border-orange-500/30 transition-all duration-300 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lottery.color }} />
                  <div>
                    <p className="text-white font-medium group-hover:text-orange-400 transition-colors">{lottery.name}</p>
                    <p className="text-sky-300/50 text-xs">{lottery.region}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-gradient-to-b from-sky-900/20 to-transparent">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 mb-4">Funcionalidades</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Todo lo que Necesitas para Ganar</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.title} className="bg-sky-900/20 border-sky-800/30 hover:border-orange-500/30 transition-all duration-300 overflow-hidden group" data-testid={`feature-${idx}`}>
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-sky-200/60">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 mb-4">Como Funciona</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Empieza en 3 Simples Pasos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, idx) => (
              <div key={step.number} className="relative text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-sky-900/30 border border-sky-800/30 mb-6">
                  <span className="text-3xl font-black bg-gradient-to-r from-sky-400 to-orange-400 bg-clip-text text-transparent">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-sky-200/60">{step.description}</p>
                {idx < STEPS.length - 1 && <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-sky-700/50 to-transparent" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection plans={PLANS} onSelectPlan={() => openAuthModal('register')} />
      <TestimonialsSection testimonials={TESTIMONIALS} />
      <FAQSection faqs={FAQS} openFaq={openFaq} setOpenFaq={setOpenFaq} />
      <CTASection onAction={() => openAuthModal('register')} />
      <FooterSection />

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialMode={authMode} />
    </div>
  );
};

export default LandingPage;
